import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { UserInfraction, useSeverityInfo } from '@/services/infractionService';
import { format } from 'date-fns';

interface InfractionHistoryProps {
  infractions: UserInfraction[];
  userName: string;
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
}

const InfractionHistory: React.FC<InfractionHistoryProps> = ({
  infractions,
  userName,
  isOpen,
  onClose,
  isLoading,
}) => {
  const { t } = useTranslation();
  const SEVERITY_INFO = useSeverityInfo();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'dismissed':
        return <XCircle className="h-4 w-4 text-gray-600" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed':
        return t('adminPanel.infractionStatus.confirmed');
      case 'pending':
        return t('adminPanel.infractionStatus.pending');
      case 'dismissed':
        return t('adminPanel.infractionStatus.dismissed');
      default:
        return status;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            {t('adminPanel.infractionHistory.title')} - {userName}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[500px] pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-muted-foreground">{t('adminPanel.infractionHistory.loading')}</p>
            </div>
          ) : infractions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('adminPanel.infractionHistory.noInfractions')}</h3>
              <p className="text-muted-foreground">
                {t('adminPanel.infractionHistory.noInfractionsDescription')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {infractions.map((infraction) => (
                <div
                  key={infraction.id}
                  className="border rounded-lg p-4 space-y-3 hover:bg-muted/30 transition-colors"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={SEVERITY_INFO[infraction.severity]?.color || 'bg-gray-100 text-gray-800'}>
                        {SEVERITY_INFO[infraction.severity]?.label || infraction.severity}
                      </Badge>
                      <Badge variant="outline" className="flex items-center gap-1">
                        {getStatusIcon(infraction.status)}
                        {getStatusLabel(infraction.status)}
                      </Badge>
                      <span className="text-sm font-medium text-foreground">
                        {infraction.infraction_type.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {format(new Date(infraction.created_at), 'dd MMM yyyy HH:mm')}
                    </span>
                  </div>

                  {/* Description */}
                  <div>
                    <p className="text-sm text-foreground">{infraction.description}</p>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                    <div className="flex items-center gap-4">
                      {infraction.related_entity_type && (
                        <span>
                          {t('adminPanel.infractionHistory.related')}: {infraction.related_entity_type}
                        </span>
                      )}
                    </div>
                    {infraction.expires_at && infraction.status === 'confirmed' && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {t('adminPanel.infractionHistory.expires')}: {format(new Date(infraction.expires_at), 'dd MMM yyyy')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default InfractionHistory;
